import { convertUTCToLocal } from "./helper";
import { parseDateSafe } from "./dateFormatters";

/**
 * Formats reading data for Out of Range reports
 * Used by both preview and PDF generation
 */
export const formatReadingData = (readings) => {
  return (
    readings?.map((reading) => {
      let readingValue = "_ _";
      let readingType = reading.reading_type || "_ _";
      let category = "_ _";

      switch (reading.reading_type) {
        case "blood_pressure":
          readingValue = `${reading.systolic}/${reading.diastolic} mmHg`;
          readingType = "Blood Pressure";
          const bpCategories = [];
          if (reading.bp_category) {
            bpCategories.push(reading.bp_category);
          }
          if (reading.heart_rate_category) {
            bpCategories.push(reading.heart_rate_category);
          }
          category = bpCategories.length > 0 ? bpCategories : "_ _";
          break;
        case "weight":
          readingValue = `${reading.weight} lbs`;
          readingType = "Weight";
          if (reading.category) {
            category = reading.category;
          }
          break;
        case "temperature":
          readingValue = `${reading.temperature}°C`;
          readingType = "Temperature";
          if (reading.category) {
            category = reading.category;
          }
          break;
        case "pulse_ox":
          readingValue = `${reading.oxygen}%`;
          readingType = "Blood Oxygen";
          if (reading.category) {
            category = reading.category;
          } else if (reading.heart_rate_category) {
            category = reading.heart_rate_category;
          }
          break;
        case "blood_glucose":
          readingValue = `${reading.glucose} mg/dL`;
          readingType = "Blood Glucose";
          if (reading.category) {
            category = reading.category;
          }
          break;
        default:
          readingValue = "_ _";
          readingType = "_ _";
          if (reading.category) {
            category = reading.category;
          }
      }

      const escalatedReviewedAt = reading?.escalated_at || reading?.reviewed_at;
      
      return {
        ...reading,
        type: readingType,
        reading_value: readingValue,
        category: category,
        date: convertUTCToLocal(reading?.recorded_at),
        escalated_reviewed_at: escalatedReviewedAt ? convertUTCToLocal(escalatedReviewedAt) : "_ _",
        name: reading?.patient_full_name || "_ _",
        location: reading?.patient_location || "_ _",
        provider: reading?.provider_full_name?.trim() || "_ _",
        interaction: reading?.interaction || "_ _",
      };
    }) || []
  );
};

/**
 * Adds recordType field to records (Escalated/Reviewed)
 */
export const addRecordType = (records) => {
  return records.map((row) => {
    let recordType = "Reviewed";
    if (row.esclated === true) {
      recordType = "Escalated";
    } else if (row.reviewed === true) {
      recordType = "Reviewed";
    }

    return {
      ...row,
      recordType: recordType,
    };
  });
};

/**
 * Filters records based on search criteria
 */
export const filterRecords = (tableData, filters) => {
  const { nameSearch, providerSearch, locationSearch, categorySearch } = filters;

  return tableData?.filter((row) => {
    const nameMatch =
      nameSearch === "" ||
      row.name?.toLowerCase() === nameSearch.toLowerCase();
    if (!nameMatch) return false;

    const providerMatch =
      providerSearch === "" ||
      row.provider?.toLowerCase() === providerSearch.toLowerCase();
    if (!providerMatch) return false;

    const locationMatch =
      locationSearch === "" ||
      row.location?.toLowerCase() === locationSearch.toLowerCase();
    if (!locationMatch) return false;

    const categoryMatch =
      categorySearch === "" ||
      (Array.isArray(row.category)
        ? row.category.some((cat) =>
            cat?.toLowerCase().includes(categorySearch.toLowerCase())
          )
        : row.category?.toLowerCase().includes(categorySearch.toLowerCase()));
    if (!categoryMatch) return false;

    return row.esclated === true || row.reviewed === true;
  });
};

/**
 * Groups and sorts records by patient name
 * Groups are sorted alphabetically, rows within each group are sorted by date (most recent first)
 */
export const groupAndSortRecords = (records) => {
  if (!records || records.length === 0) return [];

  // Group by patient name
  const grouped = {};
  records.forEach((row) => {
    const patientName = row.name || "_ _";
    if (!grouped[patientName]) {
      grouped[patientName] = [];
    }
    grouped[patientName].push(row);
  });

  // Sort groups alphabetically
  const sortedGroupNames = Object.keys(grouped).sort((a, b) => {
    if (a === "_ _") return 1;
    if (b === "_ _") return -1;
    return a.localeCompare(b);
  });

  // Flatten grouped data (without headers)
  const result = [];
  sortedGroupNames.forEach((patientName) => {
    // Sort rows within group by date (most recent first)
    const sortedRows = [...grouped[patientName]].sort((a, b) => {
      const dateA = parseDateSafe(a.date) || new Date(0);
      const dateB = parseDateSafe(b.date) || new Date(0);
      return dateB - dateA; // Most recent first
    });

    // Add all rows for this patient
    result.push(...sortedRows);
  });

  return result;
};

